// src/components/marketing/aniversariantes/flyer-desenho.js
// =======================================================================
// A ARTE DOS ANIVERSARIANTES, desenhada num <canvas>.
// =======================================================================
// ⚠️⚠️ POR QUE CANVAS E NÃO HTML + html2canvas (que era como a ferramenta
//    antiga fazia): ali a arte era DOM e a exportação tirava uma "foto" dela com
//    a biblioteca html2canvas — que não renderiza `<use href="#icone">` e obriga
//    a clonar o flyer para fora da tela antes de capturar (os dois remendos
//    estão no arquivo original). Desenhando direto no canvas, **o que se vê na
//    tela É o arquivo que sai**: não há segunda renderização para divergir.
//
// MEDIDAS: a arte tem 1200×850 px de projeto e é exportada em A4 paisagem
// (1754×1240 @150dpi) — a proporção não é idêntica, e a esticada é de
// propósito (herdada): todo PNG/PDF sai no mesmo tamanho de página.
//
// ⚠️ O TÍTULO DESLIZA. Com pouca gente ele desce (fica perto dos nomes); com
//    muita, sobe para liberar espaço para a lista. É o que evita a arte com dois
//    nomes perdidos no meio da página.
// ⚠️ ACIMA DE 4 PESSOAS a lista vira DUAS COLUNAS: numa coluna só, a fonte
//    encolheria até ninguém conseguir ler de longe.
// =======================================================================

import { MESES, nomeNaArte, tituloDeNome } from './flyer-texto';

export const FLYER = { W: 1200, H: 850 };
export const A4 = { W: 1754, H: 1240 };   // A4 paisagem a ~150dpi

// --- zona das linhas e deslize do título (px do projeto) ---
const TITULO = { W: 700, H: 184, TOPO_MIN: 38, TOPO_MAX: 110, CONTAGEM_CHEIA: 8 };
const GAP_DEPOIS_DO_TITULO = 35;
const ZONA_FIM = 686;              // limite fixo, logo acima da decoração do rodapé
const ZONA_X = 200, ZONA_W = 800;

// --- linhas ---
const MAX_UMA_COLUNA = 4;
const GAP_COLUNAS = 24;
const SLOT_BASE = 76, SLOT_TETO = 100, ALTURA_BASE = 66, GAP_BASE = 10;
// Sobra de espaço vai mais para BAIXO do que para cima: os nomes ficam colados
// ao título em vez de flutuarem no meio da página.
const FATIA_DE_SOBRA_EM_CIMA = 0.22;

const VERMELHO = ['#DB2530', '#B71C24'];
const DOURADO = ['#FFCE3F', '#F0A800'];
const TINTA_NOME = '#241A15';
const TINTA_CARGO = '#9A8677';

// Ícones da arte, nos mesmos traços do original (viewBox 24×24).
const ICONE_CALENDARIO_CONTORNO = 'M4 5h16a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1z';
const ICONE_CALENDARIO_HASTES = 'M8 3v4M16 3v4M3 10h18';
const ICONE_ARABESCO = 'M4 17c4 2 8 2 10-2s-2-6-5-4 0 8 5 8 8-4 8-8';

/** Onde o título fica, dado quantas pessoas a arte tem. */
export function topoDoTitulo(quantidade) {
    const q = Math.max(quantidade, 1);
    const t = Math.min(q, TITULO.CONTAGEM_CHEIA);
    const fracao = (t - 1) / (TITULO.CONTAGEM_CHEIA - 1);
    return TITULO.TOPO_MAX - (TITULO.TOPO_MAX - TITULO.TOPO_MIN) * fracao;
}

/**
 * Altura de cada linha para `quantidade` linhas caberem na zona.
 * Com pouca gente as linhas CRESCEM (até o teto) em vez de deixar buraco; com
 * muita, encolhem juntas para todo mundo caber na mesma página.
 */
export function medidasDaLinha(quantidade, teto, alturaZona) {
    const slot = Math.min(teto || SLOT_BASE, alturaZona / quantidade);
    const gap = slot * (GAP_BASE / SLOT_BASE);
    const altura = slot - gap;
    return { altura, gap, escala: altura / ALTURA_BASE };
}

/** A lista desta arte vai em duas colunas? */
export function duasColunas(quantidade) {
    return quantidade > MAX_UMA_COLUNA;
}

// -----------------------------------------------------------------------
// Utilitários de desenho
// -----------------------------------------------------------------------
function retanguloArredondado(ctx, x, y, w, h, r) {
    const raio = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + raio, y);
    ctx.arcTo(x + w, y, x + w, y + h, raio);
    ctx.arcTo(x + w, y + h, x, y + h, raio);
    ctx.arcTo(x, y + h, x, y, raio);
    ctx.arcTo(x, y, x + w, y, raio);
    ctx.closePath();
}

function comSombra(ctx, { cor, desfoque, deslocamentoY }, desenhar) {
    ctx.save();
    ctx.shadowColor = cor;
    ctx.shadowBlur = desfoque;
    ctx.shadowOffsetY = deslocamentoY;
    desenhar();
    ctx.restore();
}

/** Desenha um ícone de viewBox 24×24 escalado para `tamanho` px em (x, y). */
function icone(ctx, caminhos, x, y, tamanho, { traco, cor, largura }) {
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(tamanho / 24, tamanho / 24);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const d of caminhos) {
        const p = new Path2D(d);
        if (traco) { ctx.strokeStyle = cor; ctx.lineWidth = largura; ctx.stroke(p); }
        else { ctx.fillStyle = cor; ctx.fill(p); }
    }
    ctx.restore();
}

/** Quebra o texto em até `maxLinhas` linhas que caibam em `larguraMax`. */
function quebrarTexto(ctx, texto, larguraMax, maxLinhas) {
    const palavras = String(texto).split(/\s+/).filter(Boolean);
    const linhas = [];
    let atual = '';
    for (const palavra of palavras) {
        const teste = atual ? `${atual} ${palavra}` : palavra;
        if (ctx.measureText(teste).width > larguraMax && atual) {
            linhas.push(atual);
            atual = palavra;
        } else {
            atual = teste;
        }
    }
    if (atual) linhas.push(atual);
    if (linhas.length <= maxLinhas) return linhas;
    // Não cabe: corta e avisa com reticências — sumir com um pedaço do nome sem
    // sinal nenhum é o pior desfecho possível numa arte de aniversário.
    const cortadas = linhas.slice(0, maxLinhas);
    let ultima = cortadas[maxLinhas - 1];
    while (ultima.length > 1 && ctx.measureText(`${ultima}…`).width > larguraMax) {
        ultima = ultima.slice(0, -1);
    }
    cortadas[maxLinhas - 1] = `${ultima.replace(/[\s,.;–-]+$/, '')}…`;
    return cortadas;
}

// -----------------------------------------------------------------------
// Uma linha da lista: bolinha do calendário + selo do dia + cartão do nome
// -----------------------------------------------------------------------
function desenharLinha(ctx, pessoa, indice, x, y, largura, m, estreita) {
    const alt = m.altura;
    // Alterna vermelho e dourado, como na arte de referência.
    const ehVermelha = indice % 2 === 0;
    const [c1, c2] = ehVermelha ? VERMELHO : DOURADO;

    // Numa coluna estreita, o "cromo" (selo, respiros) encolhe MAIS que a linha
    // — é o que sobra de largura para o nome quebrar em duas linhas em vez de
    // ser cortado.
    const larguraSelo = (estreita ? 76 : 96) * m.escala;
    const respiro = (estreita ? 12 : 24) * m.escala;
    const entre = (estreita ? 8 : 14) * m.escala;

    // 1) bolinha branca com o calendário
    comSombra(ctx, { cor: 'rgba(60,40,20,.18)', desfoque: 8, deslocamentoY: 3 }, () => {
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(x + alt / 2, y + alt / 2, alt / 2, 0, Math.PI * 2);
        ctx.fill();
    });
    const tamIcone = 28 * m.escala;
    icone(ctx, [ICONE_CALENDARIO_CONTORNO, ICONE_CALENDARIO_HASTES],
        x + alt / 2 - tamIcone / 2, y + alt / 2 - tamIcone / 2, tamIcone,
        { traco: true, cor: '#D6202B', largura: 2 });

    // 2) selo do dia
    const xSelo = x + alt + entre;
    comSombra(ctx, { cor: 'rgba(60,20,20,.25)', desfoque: 10, deslocamentoY: 4 }, () => {
        const grad = ctx.createLinearGradient(xSelo, y, xSelo + larguraSelo, y + alt);
        grad.addColorStop(0, c1);
        grad.addColorStop(1, c2);
        ctx.fillStyle = grad;
        retanguloArredondado(ctx, xSelo, y, larguraSelo, alt, 14 * m.escala);
        ctx.fill();
    });
    ctx.fillStyle = ehVermelha ? '#fff' : '#3A2600';
    ctx.font = `600 ${34 * m.escala}px Fredoka, Poppins, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(pessoa.dia).padStart(2, '0'), xSelo + larguraSelo / 2, y + alt / 2);

    // 3) cartão branco com nome e cargo
    const xCartao = xSelo + larguraSelo + entre;
    const larguraCartao = x + largura - xCartao;
    comSombra(ctx, { cor: 'rgba(60,40,20,.14)', desfoque: 8, deslocamentoY: 3 }, () => {
        ctx.fillStyle = '#fff';
        retanguloArredondado(ctx, xCartao, y, larguraCartao, alt, 16 * m.escala);
        ctx.fill();
    });

    const tamArabesco = 18 * m.escala;
    const larguraTexto = larguraCartao - respiro * 2 - tamArabesco - 10 * m.escala;

    // O cargo só aparece quando a linha tem altura para ele — abaixo disso
    // viraria um fiapo ilegível colado no nome.
    const mostraCargo = !!(pessoa.cargo && pessoa.cargo.trim()) && m.escala >= 0.55;
    const tamNome = Math.max(10, 20 * m.escala);
    ctx.font = `700 ${tamNome}px Poppins, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    const linhas = quebrarTexto(ctx, nomeNaArte(pessoa.nome), larguraTexto, 2);

    const alturaLinha = tamNome * 1.08;
    const alturaBloco = linhas.length * alturaLinha + (mostraCargo ? 12 * m.escala * 1.4 : 0);
    let cursorY = y + alt / 2 - alturaBloco / 2 + tamNome * 0.85;
    ctx.fillStyle = TINTA_NOME;
    for (const linha of linhas) {
        ctx.fillText(linha, xCartao + respiro, cursorY);
        cursorY += alturaLinha;
    }
    if (mostraCargo) {
        ctx.font = `500 ${Math.max(8, 12 * m.escala)}px Poppins, sans-serif`;
        ctx.fillStyle = TINTA_CARGO;
        const cargo = quebrarTexto(ctx, tituloDeNome(pessoa.cargo), larguraTexto, 1)[0];
        ctx.fillText(cargo, xCartao + respiro, cursorY + 2 * m.escala);
    }

    icone(ctx, [ICONE_ARABESCO],
        xCartao + larguraCartao - respiro - tamArabesco, y + alt / 2 - tamArabesco / 2, tamArabesco,
        { traco: true, cor: ehVermelha ? '#D6202B' : '#F0A800', largura: 2.4 });
}

// -----------------------------------------------------------------------
// A arte inteira
// -----------------------------------------------------------------------
/**
 * Desenha a arte de uma filial num contexto 2D de 1200×850.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {{pessoas: Array, mes: number, imagens: {fundo: HTMLImageElement, titulo: HTMLImageElement}}} dados
 *        `mes` é 1–12.
 */
export function desenharFlyer(ctx, { pessoas, mes, imagens }) {
    const { W, H } = FLYER;
    ctx.save();
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, W, H);
    if (imagens?.fundo) ctx.drawImage(imagens.fundo, 0, 0, W, H);

    const topoTitulo = topoDoTitulo(pessoas.length);
    if (imagens?.titulo) {
        ctx.drawImage(imagens.titulo, (W - TITULO.W) / 2, topoTitulo, TITULO.W, TITULO.H);
    }

    // Faixa do mês, centrada na base do título (pílula branca + texto vermelho).
    const rotulo = (MESES[mes - 1] || '').toUpperCase();
    ctx.font = '700 19px Fredoka, Poppins, sans-serif';
    if ('letterSpacing' in ctx) ctx.letterSpacing = '2px';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const larguraRotulo = ctx.measureText(rotulo).width;
    const yRotulo = topoTitulo + TITULO.H;
    ctx.fillStyle = '#fff';
    ctx.fillRect(W / 2 - larguraRotulo / 2 - 12, yRotulo - 15, larguraRotulo + 24, 30);
    ctx.fillStyle = '#B31820';
    ctx.fillText(rotulo, W / 2, yRotulo);
    if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';

    const zonaTopo = topoTitulo + TITULO.H + GAP_DEPOIS_DO_TITULO;
    const zonaAltura = ZONA_FIM - zonaTopo;

    if (!pessoas.length) {
        ctx.font = '600 18px Poppins, sans-serif';
        ctx.fillStyle = '#8a7768';
        ctx.fillText('Nenhum aniversariante neste mês.', W / 2, zonaTopo + zonaAltura / 2);
        ctx.restore();
        return;
    }

    if (duasColunas(pessoas.length)) {
        const metade = Math.ceil(pessoas.length / 2);
        const m = medidasDaLinha(metade, null, zonaAltura);
        // As duas colunas partem da MESMA altura (a da coluna mais longa), senão
        // cada uma se centraria sozinha e as linhas ficariam desalinhadas.
        const alturaBloco = metade * (m.altura + m.gap) - m.gap;
        const deslocamento = Math.max(0, (zonaAltura - alturaBloco) * FATIA_DE_SOBRA_EM_CIMA);
        const larguraColuna = (ZONA_W - GAP_COLUNAS) / 2;
        [pessoas.slice(0, metade), pessoas.slice(metade)].forEach((coluna, iCol) => {
            const x = ZONA_X + iCol * (larguraColuna + GAP_COLUNAS);
            coluna.forEach((pessoa, i) => {
                const y = zonaTopo + deslocamento + i * (m.altura + m.gap);
                desenharLinha(ctx, pessoa, i, x, y, larguraColuna, m, true);
            });
        });
    } else {
        const m = medidasDaLinha(pessoas.length, SLOT_TETO, zonaAltura);
        const alturaBloco = pessoas.length * (m.altura + m.gap) - m.gap;
        const deslocamento = Math.max(0, (zonaAltura - alturaBloco) * FATIA_DE_SOBRA_EM_CIMA);
        pessoas.forEach((pessoa, i) => {
            const y = zonaTopo + deslocamento + i * (m.altura + m.gap);
            desenharLinha(ctx, pessoa, i, ZONA_X, y, ZONA_W, m, false);
        });
    }
    ctx.restore();
}

/**
 * A arte esticada para o tamanho exato de uma A4 paisagem — é assim que ela sai
 * em PNG e em PDF, sempre no mesmo tamanho de página.
 */
export function canvasEmA4(canvasOrigem) {
    const destino = document.createElement('canvas');
    destino.width = A4.W;
    destino.height = A4.H;
    const ctx = destino.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, A4.W, A4.H);
    ctx.drawImage(canvasOrigem, 0, 0, A4.W, A4.H);
    return destino;
}
